import React, { useEffect, useState } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  FormField,
  FormConfig,
  FormService,
} from "@shared/services/FormServices";
import { useNotificationChoices } from "@features/ux/hooks";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  usePatchRecipientList,
  usePostRecipientList,
  useRecipientListDetail,
} from "../../hooks";
import { useLocation, useParams } from "react-router-dom";
import { logEvent } from "@shared/analytics/analytics";
import { Button } from "@shared/ui/button";
import { Loader2 } from "lucide-react";

type Props = {
  stepHelpers?: {
    getStepData: (index: number) => any;
    setStepData: (index: number, data: any) => void;
    setDataToStep: (index: number, name: string, value: any) => void;
  };
  onNext?: () => void;
};

const NewListStepOne: React.FC<Props> = ({ stepHelpers, onNext }) => {
  const { action, id } = useParams();
  const [editId, setEditId] = useState<string | null>(null);

  const isEdit = action === "edit" && !!id;
  const remoteUtilityId = useRemoteUtilityId();
  // Call the hook only if in edit mode
  const { data: recipientListDetail } = useRecipientListDetail(
    remoteUtilityId,
    id!,
    isEdit
  );

  const { data: contactTypeChoice } = useNotificationChoices("recipient_type");
  const contactTypeList = mapToLabelValue(contactTypeChoice);

  const { mutate: createRecipientList, isPending: isPending } =
    usePostRecipientList();
  const { mutate: updateRecipientList, isPending: isUpdatePending } =
    usePatchRecipientList(id, {
      onSuccess: () => {},
    });

  const fields: FormField[] = [
    {
      name: "recipientType",
      type: "select",
      label: "Contact Type",
      required: true,
      placeholder: "Select contact type",
      options: contactTypeList,
      disabled: isEdit, // Disable in edit mode
    },
    {
      name: "name",
      type: "text",
      label: "List Name",
      required: true,
      placeholder: "Enter list name",
      disabled: isEdit, // Disable in edit mode
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      required: false,
      placeholder: "Enter a description for this list",
      rows: 4,
      disabled: isEdit, // Disable in edit mode
    },
    {
      name: "tags",
      type: "text",
      label: "Tags",
      required: true,
      placeholder: "Comma-separated tags (e.g. summer, campaign, vip)",
      disabled: isEdit, // Disable in edit mode
    },
  ];

  const config: FormConfig = {
    fields,
    layout: "grid",
    showSubmitButton: true,
    showResetButton: !isEdit, // Hide reset button in edit mode
    resetOnSubmit: false,
    submitButtonText: "Next",
    resetButtonText: "Clear",
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      label: "text-sm text-gray-800 font-semibold",
      input: `rounded-md border px-3 py-2 border-gray-300 w-full ${
        isEdit ? "bg-gray-50 cursor-not-allowed" : ""
      }`,
      select: `rounded-md border px-3 py-2 border-gray-300 ${
        isEdit ? "bg-gray-50 cursor-not-allowed" : ""
      }`,
      textarea: `rounded-md border px-3 py-2 border-gray-300 ${
        isEdit ? "bg-gray-50 cursor-not-allowed" : ""
      }`,
      error: "text-sm text-red-500 mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
  };

  const form = FormService.createForm(config);

  // Restore step data when component mounts (for both create and edit flows)
  useEffect(() => {
    const savedStepData = stepHelpers?.getStepData(0);

    if (savedStepData) {
      // Restore form data from saved step data
      const formData = {
        ...savedStepData,
        tags: Array.isArray(savedStepData.tags)
          ? savedStepData.tags.join(", ")
          : savedStepData.tags || "",
      };

      FormService.populateForm(form, formData, fields);
    }
  }, []); // Run once on mount

  // Populate form fields from API data in edit mode
  useEffect(() => {
    if (!isEdit || !recipientListDetail?.results?.[0]) return;

    const details = recipientListDetail.results[0];
    const tags = details.tags || [];

    const initialData = {
      name: details.name,
      recipientType: details.recipient_type || details.recipientType,
      description: details.description,
      tags, // keep as array for step data
    };

    // Determine filterType based on which filters have values
    let filterType = "";
    if (
      details.filters?.category?.length > 0 ||
      details.filters?.subcategory?.length > 0
    ) {
      filterType = "CATEGORY";
    } else if (
      details.filters?.area?.length > 0 ||
      details.filters?.sub_area?.length > 0 ||
      details.filters?.premise?.length > 0
    ) {
      filterType = "TERRITORY";
    }

    const secondStepData = {
      id: details.id,
      sourceType: details.filterType || "STATIC",
      filterType: filterType,
      category: details.filters?.category || [],
      subCategory: details.filters?.subcategory || [],
      area: details.filters?.area || [],
      subArea: details.filters?.sub_area || [],
      premise: details.filters?.premise || [],
      recipientData: {
        recipientIds: details.recipientData?.recipientIds || [],
      },
      totalRecipients: details.totalRecipients,
    };

    // Set to step helpers
    stepHelpers?.setStepData(0, initialData);
    stepHelpers?.setStepData(1, secondStepData);

    // Populate form directly
    FormService.populateForm(form, initialData, fields);

    // Format tags for form field
    form.setValue("tags", tags.join(", "));
  }, [isEdit, recipientListDetail]);

  const handleSubmit = (data: any) => {
    if (isEdit) {
      // In edit mode, just proceed to next step without updating basic info
      onNext?.();
      return;
    }

    const tags = data.tags?.split(",").map((tag) => tag.trim()) ?? [];
    const payload = {
      ...data,
      tags,
      remoteUtilityId,
    };

    // Save the current form data
    stepHelpers?.setStepData(0, { ...data, tags });

    // Check if we already have an ID from a previous API call
    const existingStepData = stepHelpers?.getStepData(1);
    if (existingStepData?.id) {
      // List already created, just navigate to next step
      onNext?.();
    } else {
      // First time creating the list
      createRecipientList(payload, {
        onSuccess: (res) => {
          logEvent("Completed List Details", {
            ...data,
            tags: data.tags
          })
          const createdId = res.result?.id;
          stepHelpers?.setDataToStep(1, "id", createdId);
          if (onNext) {
            onNext();
          }
        },
      });
    }
  };

  const handleReset = () => {
    FormService.populateForm(form, {}, fields);
  };

  const handleFieldChange = (fieldName: string, value: any) => {};

  const renderCustomFooter = () => (
    <div className="flex justify-between gap-3 mt-6 pt-4 border-t">
      {!isEdit && (
        <Button
          type="button"
          variant="outline"
          onClick={handleReset}
          disabled={isPending || isUpdatePending}
        >
          Clear
        </Button>
      )}
      {isEdit && <div></div>} {/* Empty div to maintain spacing in edit mode */}
      <Button
        type="submit"
        disabled={isPending || isUpdatePending}
        onClick={form.handleSubmit(handleSubmit)}
      >
        {isPending || isUpdatePending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {isEdit ? "Loading..." : "Creating..."}
          </>
        ) : (
          "Next"
        )}
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      {isEdit && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Basic list details cannot be modified in edit
            mode. You can modify filters and recipients in the next step.
          </p>
        </div>
      )}
      <DynamicForm
        fields={fields}
        form={form}
        config={config}
        onSubmit={handleSubmit}
        onReset={handleReset}
        onFieldChange={handleFieldChange}
        gridColumns={1}
        responsiveColumns={{ sm: 1, md: 2 }}
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};

export default NewListStepOne;
